import React, { useEffect, useState } from "react";
import Styles from "./CompanyDetails.module.css";
import Switch from "@mui/material/Switch";
import { styled } from "@mui/material/styles";

import SelectInput from "../../../commons/Inputs/Select/SelectInput";
import {
  apiAssignCompanyTrader,
  getAssignedCompanyTraders,
  UnassignCompanyTrader,
} from "../../../Services/GNA/AlterApi";
import {
  ErrorMessage,
  SuccessMessage,
} from "../../../commons/Toast/ErrorMessage";
import { useDispatch, useSelector } from "react-redux";
import { fetchCompanyById } from "../../../Redux/Slices/gnaAdminSlice";
import TraderEntity from "./TraderEntity/TraderEntity";

const AntSwitch = styled(Switch)(({ theme }) => ({
  width: 28,
  height: 16,
  padding: 0,
  display: "flex",
  "&:active": {
    "& .MuiSwitch-thumb": {
      width: 15,
    },
    "& .MuiSwitch-switchBase.Mui-checked": {
      transform: "translateX(9px)",
    },
  },
  "& .MuiSwitch-switchBase": {
    padding: 2,
    "&.Mui-checked": {
      transform: "translateX(12px)",
      color: "#fff",
      "& + .MuiSwitch-track": {
        opacity: 1,
        backgroundColor: "var(--primary-color-text)",
        ...theme.applyStyles("dark", {
          backgroundColor: "var(--primary-color-text)",
        }),
      },
    },
  },
  "& .MuiSwitch-thumb": {
    boxShadow: "0 2px 4px 0 rgb(0 35 11 / 20%)",
    width: 12,
    height: 12,
    borderRadius: 6,
    transition: theme.transitions.create(["width"], {
      duration: 200,
    }),
  },
  "& .MuiSwitch-track": {
    borderRadius: 16 / 2,
    opacity: 1,
    backgroundColor: "rgba(149, 148, 148, 0.25)",
    boxSizing: "border-box",
    ...theme.applyStyles("dark", {
      backgroundColor: "rgba(255,255,255,.35)",
    }),
  },
}));

const ConfigurationDetails = ({ traders, isTraderOnly }) => {
  const company = useSelector(
    (state) => state?.gnaAdminSlice?.fetchCompanyById?.data,
  );
  const companyId = company?.companyId;
  const userId = company?.companyAdminUserId;
  const dispatch = useDispatch();
  const [selectedTraderIds, setSelectedTraderIds] = useState([]);
  const [selectedEntity, setSelectedEntity] = useState(null);
  const [loadingTraders, setLoadingTraders] = useState(false); 

  const handleAssignTrader = async (data) => {
    let res = await apiAssignCompanyTrader({
      traderUserId: data?.id,
      companyId: companyId,
      consumerId: data?.type === "consumer" ? data?.consumerId : null,
      generatorId: data?.type === "generator" ? data?.generatorId : null,
    });
    if (res?.data?.code === 200) {
      SuccessMessage(res?.data?.message);
      dispatch(fetchCompanyById(companyId));
    } else {
      ErrorMessage(res?.data?.error);
    }
  };

  const handleToggleTrader = (trader) => {
    if (selectedTraderIds.includes(trader.id)) {
      // Unassign with loader
      setLoadingTraders(true);
      unAssign(trader).finally(() => setLoadingTraders(false));
      setSelectedTraderIds((prev) => prev.filter((id) => id !== trader.id));
    } else {
      setSelectedTraderIds((prev) => [...prev, trader.id]);
      handleAssignTrader({
        id: trader.id,
        fullName: trader.name,
        type: selectedEntity?.type,
        consumerId:
          selectedEntity?.type === "consumer" ? selectedEntity?.id : null,
        generatorId:
          selectedEntity?.type === "generator" ? selectedEntity?.id : null,
      });
    }
  };

  // Update unAssign to accept trader
  async function unAssign(trader) {
    const params = {
      companyId: companyId,
      traderUserId: trader.id,
    };
    if (selectedEntity?.type === "consumer") {
      params.consumerId = selectedEntity.id;
    } else if (selectedEntity?.type === "generator") {
      params.generatorId = selectedEntity.id;
    }

    const res = await UnassignCompanyTrader(params);

    if (res?.data?.code === 200) {
      SuccessMessage(res?.data?.message);
      dispatch(fetchCompanyById(companyId));
      // Optionally refresh assigned traders here if needed
    } else {
      ErrorMessage(res?.data?.error);
    }
  }

  useEffect(() => {
    async function getAssignedTraders() {
      setLoadingTraders(true);
      const params = {
        companyId: companyId,
      };
      if (selectedEntity?.type === "consumer") {
        params.consumerId = selectedEntity.id;
      } else if (selectedEntity?.type === "generator") {
        params.generatorId = selectedEntity.id;
      }

      const res = await getAssignedCompanyTraders(params);

      if (res?.data?.code === 200) {
        const assignedTraders = res?.data?.data || [];
        const assignedTraderIds = assignedTraders.map(
          (trader) => trader.userId,
        );
        setSelectedTraderIds(assignedTraderIds);
      } else {
        ErrorMessage(res?.data?.error);
      }
      setLoadingTraders(false);
    }
    if (selectedEntity && companyId) {
      getAssignedTraders();
    }
  }, [companyId, selectedEntity]);

  const consumers = useSelector(
    (state) => state?.gnaAdminSlice?.fetchConsumersCompanyId?.data,
  );
  const generators = useSelector(
    (state) => state?.gnaAdminSlice?.fetchGeneratorsCompanyId?.data,
  );

  return (
    <>
      {isTraderOnly ? (
        <TraderEntity companyId={companyId} userId={userId} />
      ) : (
        <div className={`display_flex_column_gap_8 ${Styles.detailsContainer}`}>
          <div className={Styles.sectionHeader}>Trader Selection</div>
          <div
            className={`display_flex_column_gap_8 ${Styles.detailItem} ${Styles.selectradersContainers}`}
          >
            {/* step 1 */}

            <div style={{ display: "flex", gap: "100px" }}>
              <div>
                <div style={{ padding: "5px", fontSize: "12px" }}>
                  <span style={{ marginBottom: "70px" }}>
                    1. Select Generators/Consumers :
                  </span>
                </div>
                <div style={{ margin: "15px 0px" }}>
                  <strong style={{ marginBottom: "70px" }}>Consumers :</strong>

                  <div style={{ width: "250px", marginTop: "5px" }}>
                    <SelectInput
                      list={consumers?.map((ele) => ({
                        id: ele?.consumerId || "",
                        name: ele?.consumerName || "",
                        type: "consumer",
                      }))}
                      onChange={(ele) => setSelectedEntity(ele)}
                      example={"Select consumer"}
                      value={
                        selectedEntity && selectedEntity.type === "consumer"
                          ? selectedEntity.id
                          : ""
                      }
                      height={35}
                    />
                  </div>
                </div>
                <div style={{ margin: "15px 0px" }}>
                  <strong style={{ marginBottom: "70px" }}>Generators :</strong>

                  <div style={{ width: "250px", marginTop: "5px" }}>
                    <SelectInput
                      list={generators?.map((ele) => ({
                        id: ele?.generatorId || "",
                        name: ele?.generatorName || "",
                        type: "generator",
                      }))}
                      onChange={(ele) => setSelectedEntity(ele)}
                      example={"Select generator"}
                      value={
                        selectedEntity && selectedEntity.type === "generator"
                          ? selectedEntity.id
                          : ""
                      }
                      height={35}
                    />
                  </div>
                </div>
              </div>

              {selectedEntity && (
                <div>
                  <div style={{ padding: "5px", fontSize: "12px" }}>
                    <span style={{ marginBottom: "70px" }}>
                      2. Assign/Unassign Traders :
                      {loadingTraders && (
                        <span
                          style={{
                            marginLeft: 8,
                            fontSize: 12,
                            color: "#1890ff",
                          }}
                        >
                          Loading...
                        </span>
                      )}
                    </span>
                  </div>

                  <div>
                    <div style={{ margin: "15px 0px" }}>
                      <strong style={{ marginBottom: "70px" }}>
                        Traders :
                      </strong>

                      <>
                        {" "}
                        <div
                          style={{
                            width: "250px",
                            marginTop: "5px",
                            maxHeight: "220px",
                            overflowY: "auto",
                            border: "1px solid #e0e0e0",
                            borderRadius: "6px",
                            background: "#fafcff",
                            boxShadow: "0 2px 8px 0 rgba(0,0,0,0.03)",
                          }}
                        >
                          <ul
                            style={{
                              listStyle: "none",
                              padding: "8px 0",
                              margin: 0,
                            }}
                          >
                            {traders?.map((trader) => (
                              <li
                                key={trader.userId}
                                style={{
                                  display: "flex",
                                  alignItems: "center",
                                  justifyContent: "space-between",
                                  borderBottom: "1px solid #eee",
                                  padding: "8px 12px",
                                }}
                              >
                                <span
                                  style={{
                                    color: "#222",
                                    fontSize: "12px",
                                  }}
                                >
                                  {trader.fullName}
                                </span>
                                <AntSwitch
                                  checked={selectedTraderIds.includes(
                                    trader.userId,
                                  )}
                                  onChange={() =>
                                    handleToggleTrader({
                                      id: trader.userId,
                                      name: trader.fullName,
                                    })
                                  }
                                  inputProps={{ "aria-label": "toggle trader" }}
                                />
                              </li>
                            ))}
                          </ul>
                        </div>
                      </>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default ConfigurationDetails;
